import { observer } from 'mobx-react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { useStore } from 'hooks/useStore';
import classes from './ticketDetailModal.module.scss';

const TicketDetailModal = () => {
  const {
    SupportTicketStore: {
      isDetailModalVisible,
      setDetailModalVisible,
      selectedTicket,
      isTicketDetailLoading,
      isSyncingStatus,
      syncTicketStatus
    }
  } = useStore();

  const handleClose = () => {
    setDetailModalVisible(false);
  };

  const handleSyncStatus = () => {
    if (selectedTicket) {
      syncTicketStatus(selectedTicket.jiraTicketId);
    }
  };

  const handleOpenInJira = () => {
    if (selectedTicket?.jiraUrl) {
      window.open(selectedTicket.jiraUrl, '_blank');
    }
  };

  const getStatusSeverity = (status: string): 'success' | 'info' | 'warning' | 'danger' => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('done') || lowerStatus.includes('resolved') || lowerStatus.includes('closed')) {
      return 'success';
    }
    if (lowerStatus.includes('progress') || lowerStatus.includes('active')) {
      return 'info';
    }
    if (lowerStatus.includes('pending') || lowerStatus.includes('waiting')) {
      return 'warning';
    }
    return 'info';
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const renderFooter = () => (
    <div className={classes.footer}>
      <Button
        label="Sync Status"
        icon={isSyncingStatus ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
        onClick={handleSyncStatus}
        disabled={isSyncingStatus}
        className="p-button-outlined"
      />
      {selectedTicket?.jiraUrl && (
        <Button
          label="Open in Jira"
          icon="pi pi-external-link"
          onClick={handleOpenInJira}
          className="p-button-outlined"
        />
      )}
      <Button label="Close" onClick={handleClose} />
    </div>
  );

  return (
    <Dialog
      header="Ticket Details"
      visible={isDetailModalVisible}
      onHide={handleClose}
      footer={renderFooter()}
      style={{ width: '700px', maxWidth: '90vw' }}
      className={classes.dialog}
    >
      {isTicketDetailLoading ? (
        <div className={classes.loading}>
          <ProgressSpinner />
          <p>Loading ticket details...</p>
        </div>
      ) : selectedTicket ? (
        <div className={classes.content}>
          {/* Header Info */}
          <div className={classes.headerInfo}>
            <div className={classes.ticketId}>
              <strong>Ticket ID:</strong> {selectedTicket.jiraTicketId}
            </div>
            <Tag
              value={selectedTicket.status}
              severity={getStatusSeverity(selectedTicket.status)}
            />
          </div>

          {/* Summary */}
          <div className={classes.section}>
            <h3>Summary</h3>
            <p>{selectedTicket.summary}</p>
          </div>

          <Divider />

          {/* Description */}
          <div className={classes.section}>
            <h3>Description</h3>
            <p className={classes.description}>{selectedTicket.description}</p>
          </div>

          <Divider />

          {/* Metadata */}
          <div className={classes.metadata}>
            <div className={classes.metadataItem}>
              <strong>Category:</strong>
              <span>{selectedTicket.category}</span>
            </div>
            <div className={classes.metadataItem}>
              <strong>Environment:</strong>
              <span>{selectedTicket.environment}</span>
            </div>
            <div className={classes.metadataItem}>
              <strong>Created:</strong>
              <span>{formatDate(selectedTicket.createdAt)}</span>
            </div>
            {selectedTicket.reporter && (
              <div className={classes.metadataItem}>
                <strong>Reporter:</strong>
                <span>{selectedTicket.reporter}</span>
              </div>
            )}
            {selectedTicket.assignee && (
              <div className={classes.metadataItem}>
                <strong>Assignee:</strong>
                <span>{selectedTicket.assignee}</span>
              </div>
            )}
            {selectedTicket.priority && (
              <div className={classes.metadataItem}>
                <strong>Priority:</strong>
                <span>{selectedTicket.priority}</span>
              </div>
            )}
          </div>

          {/* Browser Info */}
          {selectedTicket.browserInfo && (
            <>
              <Divider />
              <div className={classes.section}>
                <h3>Browser Information</h3>
                <div className={classes.browserInfo}>
                  <div className={classes.infoItem}>
                    <strong>URL:</strong> <span>{selectedTicket.browserInfo.url}</span>
                  </div>
                  <div className={classes.infoItem}>
                    <strong>Browser:</strong> <span>{selectedTicket.browserInfo.userAgent}</span>
                  </div>
                  <div className={classes.infoItem}>
                    <strong>Platform:</strong> <span>{selectedTicket.browserInfo.platform}</span>
                  </div>
                  <div className={classes.infoItem}>
                    <strong>Resolution:</strong> <span>{selectedTicket.browserInfo.screenResolution}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Comments */}
          {selectedTicket.comments && selectedTicket.comments.length > 0 && (
            <>
              <Divider />
              <div className={classes.section}>
                <h3>Comments</h3>
                <div className={classes.comments}>
                  {selectedTicket.comments.map((comment) => (
                    <div key={comment.id} className={classes.comment}>
                      <div className={classes.commentHeader}>
                        <strong>{comment.author}</strong>
                        <span className={classes.commentDate}>{formatDate(comment.createdAt)}</span>
                      </div>
                      <p>{comment.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Attachments */}
          {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
            <>
              <Divider />
              <div className={classes.section}>
                <h3>Attachments</h3>
                <div className={classes.attachments}>
                  {selectedTicket.attachments.map((attachment) => (
                    <div key={attachment.id} className={classes.attachment}>
                      <i className="pi pi-file" />
                      <span>{attachment.filename}</span>
                      {attachment.url && (
                        <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                          <i className="pi pi-download" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={classes.noData}>
          <p>No ticket data available.</p>
        </div>
      )}
    </Dialog>
  );
};

export default observer(TicketDetailModal);
